import { Col, Container, Row } from "react-bootstrap"
import { graphql, Link } from "gatsby"
import React, { useEffect, useState } from "react"

import ApButton from "../components/Global/Common/ApButton/ApButton"
import Layout from "../components/Global/Layout/Layout"
import PostPreview from "../components/BlogPage/Tabs/PostPreview"
import SEO from "../components/Global/SEO/seo"
import { colors } from "../utils/styles"
import styled from "styled-components"
import { navigateTo } from "../utils/hooks"
import { contentStyles } from "../components/Flexipage/Global/FlexipageStyles"
import {
  StatamicHtml,
  prefixStatamicAssetUrl,
} from "../utils/statamicAssets"

const ArticleHeader = styled.div`
  background: ${({ imageExists }) =>
    imageExists
      ? `linear-gradient(
    180deg,
    rgba(59, 92, 167, 1) 70%,
    rgba(255, 255, 255, 1) 30%
  )`
      : `linear-gradient(
    180deg,
    rgba(59, 92, 167, 1) 100%,
    rgba(255, 255, 255, 1) 30%
  )`};
  @media (max-width: 992px) {
    background: ${colors.secondaryBlue3};
  }
  padding-top: 88px;
  .title {
    color: #4cb9ef;
    font-weight: 800;
    @media (min-width: 992px) {
      font-size: 3rem;
    }
  }
  .category,
  .created-at {
    color: #55b9a3;
    font-weight: 600;
    font-size: 1.4rem;
  }
  .blog-header-image {
    width: 100%;
    object-fit: cover;
    aspect-ratio: 21 / 9;
    @media (min-width: 992px) {
      border-radius: 16px;
    }
  }

  .header-author-details{
    display: flex;
    gap: 15px;
    align-items: center;

    .author-profile-image{
      width: 50px;
      height: 50px;
      object-fit: cover;
      border-radius: 50%;
    }
  }
`
const ArticleBody = styled.article`
  ${contentStyles}
`

const AuthorDetails = styled.section`
  .author-details{
    background: linear-gradient(180deg,rgba(85,185,163,0.16) 0%,rgba(143,197,234,0.4) 100%);
    border-radius: 16px;
    display: grid;
    grid-template-columns: 150px 1fr;
    justify-content: center;
    align-items: center;
    gap: 25px;

    .author-profile-image{
      width: 150px;
      height: 150px;
      object-fit: cover;
      border-radius: 50%;
    }

    .author-name{
      font-size: 1.25em;
      line-height: 1.25em;
    }

    @media screen and (max-width: 767px) {
      grid-template-columns: 1fr;
      text-align: center;

      .author-profile-image{
        margin: auto;
      }
    }
  }
`

const BlogArticle = ({ data }) => {
  const [pageUrl, setPageUrl] = useState("")
  useEffect(() => {
    setPageUrl(window.location.href)
  }, [])

  const entry = data && data.statamic && data.statamic.entry
  if (!entry) return null

  const {
    title,
    slug,
    published_on: publishedOn,
    last_modified: lastModified,
    article,
    blog_categories: blogCategories,
    preview_image: previewImage,
    blog_authors: blogAuthors,
  } = entry

  const articleHtml =
    typeof article === "string"
      ? article
      : (article || [])
          .map((block) => (block && block.text) || "")
          .join("")

  const category = (blogCategories || []).map((c) => ({
    slug: c.slug,
    name: c.title,
  }))
  const showCategory = category.length > 0 && category[0].slug !== "internal"

  const previewImageUrl =
    previewImage &&
    prefixStatamicAssetUrl(previewImage.permalink || previewImage.url)
  const previewImageAlt =
    previewImage && previewImage.alt ? previewImage.alt : title || "Article Lead Image"

  const author = (blogAuthors && blogAuthors[0]) || null
  const authorImageUrl =
    author && author.profile_picture
      ? prefixStatamicAssetUrl(
          author.profile_picture.permalink || author.profile_picture.url
        )
      : null
  const authorImageAlt =
    author && author.profile_picture && author.profile_picture.alt
      ? author.profile_picture.alt
      : "Article Author"
  const authorName = author ? author.name : null
  const authorDescription = author ? author.description : null
  const authorEmail = author ? author.email : null
  const linkedinUrl = author ? author.linkedin_url : null

  let datePublished = ""
  let dateModified = ""
  try {
    const pub = new Date(publishedOn)
    let mod = new Date(lastModified || publishedOn)
    if (pub > mod) mod = pub
    if (!isNaN(pub.getTime())) datePublished = pub.toISOString().split("T")[0]
    if (!isNaN(mod.getTime())) dateModified = mod.toISOString().split("T")[0]
  } catch (e) {}

  let schema = {}
  if (authorName && title && datePublished && dateModified && slug) {
    schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": "https://apexx.global/blog/" + slug + "/",
      },
      author: {
        "@type": "Person",
        name: authorName,
      },
      publisher: {
        "@type": "Organization",
        name: "Apexx Global",
        url: "https://apexx.global",
        logo: {
          "@type": "ImageObject",
          url:
            "https://apexx.global/static/4fb98955b0c4c8b17a0a77af65307c2f/14b42/apexx-logo.jpg",
        },
      },
      headline: title,
      datePublished,
      dateModified,
    }
    if (linkedinUrl) schema.author.url = linkedinUrl
  }

  const latestEntries =
    (data && data.statamic && data.statamic.latest && data.statamic.latest.data) || []
  const latestPosts = latestEntries.map((node) => ({
    node: {
      id: node.id,
      title: node.title,
      slug: node.slug,
      publishedOn: node.published_on,
      externalLink: node.external_link,
      previewImage: node.preview_image
        ? {
            url: prefixStatamicAssetUrl(
              node.preview_image.permalink || node.preview_image.url
            ),
            title: node.preview_image.alt || node.title,
          }
        : null,
      category: (node.blog_categories || []).map((c) => ({
        slug: c.slug,
        name: c.title,
      })),
    },
  }))

  return (
    <Layout>
      <SEO title={title} additionalSchema={schema} />
      <div>
        <ArticleHeader imageExists={!!previewImageUrl}>
          <Container className="position-relative">
            {showCategory && (
              <div className=" px-3 px-lg-0">
                <Link
                  to={`/blog/${category[0].slug}/`}
                  className="category text-uppercase"
                >
                  {category[0].name}
                </Link>
              </div>
            )}

            <h1 className="title my-5 px-3 px-lg-0">{title}</h1>
            <div className="post-details mb-5 d-block d-md-flex justify-content-between align-items-center">
              <div className={`created-at px-3 px-lg-0`}>
                <span>{`${publishedOn || ""} . 4 minutes`}</span>
              </div>
              {authorImageUrl && (
                <div className="header-author-details px-3 px-lg-0 text-white mt-3 mt-md-0">
                  <a href="#aboutTheAuthor">
                    <img
                      src={authorImageUrl}
                      className="author-profile-image"
                      alt={authorImageAlt}
                    />
                  </a>
                  <a href="#aboutTheAuthor" className="text-white">
                    {authorName}
                  </a>
                </div>
              )}
            </div>
            {previewImageUrl && (
              <img
                src={previewImageUrl}
                className="blog-header-image position-relative"
                alt={previewImageAlt}
              />
            )}
          </Container>
        </ArticleHeader>
        <Container className="my-5 px-3 px-lg-0">
          <StatamicHtml as={ArticleBody} className="mb-5" html={articleHtml} />
          {authorImageUrl && (
            <AuthorDetails>
              <hr />
              <h2 id="aboutTheAuthor" className="mt-4">
                About the author
              </h2>
              <div className="author-details p-4 mt-4 mb-5">
                <img
                  src={authorImageUrl}
                  className="author-profile-image"
                  alt={authorImageAlt}
                />
                <div className="author-bio">
                  <div className="author-name font-weight-bold ambit mb-3">
                    {authorName}
                  </div>
                  <p className="job-title">{authorDescription}</p>
                  {authorEmail && (
                    <p className="p-0 m-0">
                      <a
                        href={"mailto:" + authorEmail}
                        className="email-address"
                      >
                        {authorEmail}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </AuthorDetails>
          )}
          <section className="pt-5 border-top d-flex flex-column align-items-center">
            <Row>
              {latestPosts.map(
                (post) =>
                  post.node.category &&
                  post.node.category.length > 0 && (
                    <Col key={post.node.id}>
                      <PostPreview post={post} showCategory={true} />
                    </Col>
                  )
              )}
            </Row>
            <div>
              <ApButton
                variant="green"
                className="pl-4 pr-4"
                onClick={(e) => navigateTo(e, "blog")}
              >
                See All
              </ApButton>
            </div>
          </section>
        </Container>
      </div>
    </Layout>
  )
}

export default BlogArticle

export const query = graphql`
  query Getpost($slug: String!) {
    statamic {
      entry(collection: "blog_posts", slug: $slug) {
        id
        title
        slug
        ... on Statamic_Entry_BlogPosts_BlogPost {
          published_on(format: "d M, Y")
          last_modified(format: "d M, Y")
          external_link
          article
          blog_categories {
            slug
            title
          }
          preview_image {
            url
            permalink
            ... on Statamic_Asset_Assets {
              alt
              width
              height
              last_modified
            }
          }
          blog_authors {
            ... on Statamic_Entry_Authors_Author {
              name
              description
              email
              linkedin_url
              profile_picture {
                url
                permalink
                ... on Statamic_Asset_Assets {
                  alt
                  width
                  height
                }
              }
            }
          }
        }
      }
      latest: entries(
        collection: "blog_posts"
        limit: 4
        sort: "published_on desc"
        filter: { blog_categories: { doesnt_contain: "internal" } }
      ) {
        data {
          id
          title
          slug
          ... on Statamic_Entry_BlogPosts_BlogPost {
            published_on(format: "d M, Y")
            external_link
            blog_categories {
              slug
              title
            }
            preview_image {
              url
              permalink
              ... on Statamic_Asset_Assets {
                alt
              }
            }
          }
        }
      }
    }
  }
`
